/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useHistory } from 'react-router';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ImportModal } from 'src/core/components/ImportModal';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { RenameModal, RenameModalDto } from 'src/core/components/RenameModal';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { useZoomLevel } from 'src/core/hooks/useZoomLevel';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { FileType } from 'src/core/types/FileType';
import { routeTable } from 'src/core/utils/routeTable';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { AppLogsImportModal } from 'src/luis/appShell/appListPage/components/AppLogsImportModal';
import {
    ConversationAppCreateModal,
    ConversationAppCreateModalSubmitDto
} from 'src/luis/appShell/appListPage/components/ConversationAppCreateModal';
import { ConversationApplicationsTable } from 'src/luis/appShell/appListPage/components/ConversationApplicationsTable';
import { ConversationAppsTableCommandBar } from 'src/luis/appShell/appListPage/components/ConversationAppsTableCommandBar';
import { EntityMigrationMessageBar } from 'src/luis/appShell/appListPage/components/EntityMigrationMessageBar';
import { luisAppListPageMessages } from 'src/luis/appShell/appListPage/luisAppListPageMessages';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';
import { useCompositeEntityMigration } from 'src/luis/modules/entities/hooks/useCompositeEntityMigration/useCompositeEntityMigration';
import { LuisId } from 'src/luis/types/Resource';
import { AzurePickerBar } from 'src/modules/azure/components/AzurePickerBar';
import { Optional } from 'utility-types';

const { styleDiv, styleComponent } = getStylistV2('ApplicationsPage');
const AppsTableWindow = styleDiv('AppsTableWindow', { height: '100%', overflowY: 'auto' });
const AppsTableContainer = styleComponent<IStackProps>(Stack)('AppsTableContainer', { overflow: 'hidden' });

const defaultModalsState = {
    isDeleteAppModalHidden: true,
    isRenameAppModalHidden: true,
    isNextStepsModalHidden: true,
    isCluMigrationModalHidden: true,
    isCreateConversationAppModalHidden: true,
    isImportConversationAppModalHidden: true,
    isImportConversationLogsModalHidden: true,
    isMigratingLuisApplicationsnModalHidden: true
};

export const LuisAppListPage = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { dispatcher, rootStore, uiStore } = useGlobalState();
    const { selection, selectedItems } = useSelection<LuisAppStore>();
    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);
    const { isCompositeEntityMigrationNeeded, appsToUpgrade, dismissEntityMigration } = useCompositeEntityMigration();
    const { isAccessibleMode } = useZoomLevel();

    const [query, setQuery] = React.useState('');
    const [fileTypeToImport, setFileTypeToImport] = React.useState<FileType>('json');
    const [sortKey, setSortKey] = React.useState<SortKeyType<LuisAppStore>>({ key: 'modifiedDate', order: 'desc' });

    const azureSession = rootStore.azureStore.session;
    const { userRole, displayedUserRole } = azureSession.serviceKey;
    const activitySource = 'ConversationApplicationsPage';
    const eventLoggerRef = React.useRef(getEventLogger(activitySource));

    const canCreateApp = Boolean(azureSession.subscriptionId) && Boolean(azureSession.csAccountStore?.path);

    const filteredApps = orderBy(
        substringSearchStrategy(rootStore.luisAppStoreHouse.items, query, 'name'),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(filteredApps, 10);

    const createAppProgressTracker = useProgressTracker<LuisId>({
        onComplete: (newAppId: LuisId) => {
            setModalsState(defaultModalsState);
            openApp(newAppId);
            uiStore.setTeachingBubble('IntroTutorial', false);
        }
    });

    React.useEffect(() => {
        if (rootStore.serviceKey.value) {
            dispatcher.dispatch('initializeApplicationsAndCultures', { context: { forceRefresh: true } });
        }
        // The account id is added to the dependency list as in AAD authentication the service key has same value regardless of changing the resource.
    }, [rootStore.serviceKey.value, rootStore.azureStore.session.csAccountStore?.id]);

    const defaultProgressTracker = useProgressTracker({
        onComplete: () => {
            setModalsState(defaultModalsState);
            selection.setAllSelected(false);
        }
    });

    /**
     * Opens the application by loading and routing to it.
     *
     * @param appId The application id to open.
     * @param versionId The version id to open.
     */
    const openApp = (appId: LuisId): void => {
        const appToOpen = rootStore.luisAppStoreHouse.findById(appId);

        history.push(routeTable.luis.applicationPage(appId, appToOpen.activeVersion));
    };

    /**
     * Creates an app with the given information.
     *
     * @param appDto The data to use to create the app.
     */
    const createApp = ({ name, culture, description, csAccountStore }: ConversationAppCreateModalSubmitDto): void => {
        let payload: Pick<LuisAppStore, 'name' | 'culture' | 'description'> & Optional<{ csAccount: AzureCsAccountStore }> = {
            name,
            culture,
            description
        };

        if (csAccountStore) {
            payload = { ...payload, csAccount: csAccountStore };
        }

        dispatcher.dispatch('createConversationApp', {
            ...payload,
            toastData: {
                running: { title: localize(luisAppListPageMessages.createAppToastStart) },
                completed: { title: localize(luisAppListPageMessages.createAppToastSuccess) },
                failed: { title: localize(luisAppListPageMessages.createAppToastFailed) }
            },
            tracker: createAppProgressTracker
        });

        setModalsState({ isCreateConversationAppModalHidden: true });

        eventLoggerRef.current.end('CreateConversationAppDialog', { appName: name });
    };

    /**
     * Imports the application selected by calling the appropriate
     * application handler method, passing it the selected id.
     */
    const importConversationApp = (file: File, name: string): void => {
        dispatcher.dispatch('importConversationApp', {
            file,
            name,
            fileType: fileTypeToImport,
            toastData: {
                running: { title: localize(luisAppListPageMessages.importAppToastStart) },
                completed: { title: localize(luisAppListPageMessages.importAppToastSuccess) },
                failed: { title: localize(luisAppListPageMessages.importAppToastFailed) }
            },
            tracker: createAppProgressTracker
        });
    };

    /**
     * Rename the application given in the arguments.
     *
     * @param data The rename data containing the new
     * name to rename the app to.
     */
    const renameApp = (data: RenameModalDto): void => {
        dispatcher.dispatch('renameApp', {
            name: data.name,
            description: selectedItems[0].description,
            toastData: {
                running: { title: localize(luisAppListPageMessages.renameAppToastStart) },
                completed: { title: localize(luisAppListPageMessages.renameAppToastSuccess) },
                failed: { title: localize(luisAppListPageMessages.renameAppToastFailed) }
            },
            tracker: defaultProgressTracker,
            context: { appId: selectedItems[0].id, appType: selectedItems[0].appType }
        });
    };

    /**
     * Exports the application selected in JSON format by calling the
     * appropriate application handler method, passing it the selected id.
     */
    const exportConversationApp = (fileType: FileType): void => {
        dispatcher.dispatch('exportConversationApp', {
            fileType,
            toastData: {
                running: { title: localize(luisAppListPageMessages.exportAppToastStart) },
                completed: { title: localize(luisAppListPageMessages.exportAppToastSuccess) },
                failed: { title: localize(luisAppListPageMessages.exportAppToastFailed) }
            },
            tracker: defaultProgressTracker,
            context: { appId: selectedItems[0].id, versionId: (selectedItems[0] as LuisAppStore).activeVersion, activitySource }
        });
    };

    /**
     * Exports the application selected for container by calling the appropriate
     * application handler method, passing it the selected id.
     */
    const exportConversationAppForContainer = (slot: string): void => {
        dispatcher.dispatch('exportConversationAppForContainer', {
            slot,
            toastData: {
                running: { title: localize(luisAppListPageMessages.exportAppToastStart) },
                completed: { title: localize(luisAppListPageMessages.exportAppToastSuccess) },
                failed: { title: localize(luisAppListPageMessages.exportAppToastFailed) }
            },
            tracker: defaultProgressTracker,
            context: { appId: selectedItems[0].id, activitySource }
        });
    };

    const openImportAppModal = (fileType: FileType) => {
        setFileTypeToImport(fileType);
        setModalsState({ isImportConversationAppModalHidden: false });
    };

    /**
     * Imports the container endpoint logs to the selected app.
     */
    const importConversationAppLogs = (file: File): void => {
        dispatcher.dispatch('importConversationAppContainerLogs', {
            logsCsvFile: file,
            toastData: {
                running: { title: localize(luisAppListPageMessages.importAppLogsToastStart) },
                completed: { title: localize(luisAppListPageMessages.importAppLogsToastSuccess) },
                failed: { title: localize(luisAppListPageMessages.importAppLogsToastFailed) }
            },
            tracker: defaultProgressTracker,
            context: { appId: selectedItems[0].id }
        });
    };

    /**
     * Exports the endpoint logs of the selected app.
     */
    const exportConversationAppLogs = (): void => {
        dispatcher.dispatch('exportConversationAppLogs', {
            toastData: {
                running: { title: localize(luisAppListPageMessages.exportAppLogsToastStart) },
                completed: { title: localize(luisAppListPageMessages.exportAppLogsToastSuccess) },
                failed: { title: localize(luisAppListPageMessages.exportAppLogsToastFailed) }
            },
            tracker: defaultProgressTracker,
            context: { appId: selectedItems[0].id }
        });
    };

    /**
     * Deletes the applications selected by calling the appropriate
     * application handler method, passing it the selected ids.
     */
    const deleteApp = (): void => {
        dispatcher.dispatch('deleteApps', {
            appIds: selectedItems.map(selectedItem => selectedItem.id),
            toastData: {
                running: { title: localize(luisAppListPageMessages.deleteAppToastStart) },
                completed: { title: localize(luisAppListPageMessages.deleteAppToastSuccess) },
                failed: { title: localize(luisAppListPageMessages.deleteAppToastFailed) }
            },
            tracker: defaultProgressTracker
        });
    };

    /**
     * Gets the placeholder to display based on the
     * current selected accounts and search query.
     */
    const getTablePlaceholder = (): string => {
        const { subscriptionId, csAccountStore } = rootStore.azureStore.session;
        if (!subscriptionId || !csAccountStore) {
            return localize(luisAppListPageMessages.noSubscriptionOrResourceGroupTablePlaceholder);
        }

        return localize(luisAppListPageMessages.emptyTablePlaceholder);
    };

    const openCreateConversationAppDialog = () => {
        setModalsState({ isCreateConversationAppModalHidden: false });
        eventLoggerRef.current.start('CreateConversationAppDialog', { appName: '' });
    };

    const dismissCreateConversationAppDialog = () => {
        setModalsState({ isCreateConversationAppModalHidden: true });
        eventLoggerRef.current.end('CreateConversationAppDialog', { appName: '' });
        eventLoggerRef.current.log('CreateConversationAppDialogDismissed');
    };

    const onQueryChange = (searchQuery: string) => {
        eventLoggerRef.current.log('ApplicationTableSearchTriggered');
        setQuery(searchQuery);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        selection.setAllSelected(false);
    };

    return (
        <>
            {isCompositeEntityMigrationNeeded && (
                <EntityMigrationMessageBar appsToUpgradeCount={appsToUpgrade.length} onDismiss={dismissEntityMigration} />
            )}

            <Page size="container-fluid" pageName={activitySource} title={localize(luisAppListPageMessages.pageTitle)}>
                <EditableTitle title={localize(luisAppListPageMessages.pageTitle)} />

                <AzurePickerBar />

                <AppsTableContainer>
                    <ConversationAppsTableCommandBar
                        query={query}
                        userRole={userRole}
                        canCreateApp={canCreateApp}
                        selectedItems={selectedItems}
                        onQueryChange={onQueryChange}
                        onImportApp={openImportAppModal}
                        onExportApp={exportConversationApp}
                        displayedUserRole={displayedUserRole}
                        onExportLogs={exportConversationAppLogs}
                        onCreateApp={openCreateConversationAppDialog}
                        onExportAppForContainer={exportConversationAppForContainer}
                        onRenameApp={() => setModalsState({ isRenameAppModalHidden: false })}
                        onDeleteApps={() => setModalsState({ isDeleteAppModalHidden: false })}
                        onImportLogs={() => setModalsState({ isImportConversationLogsModalHidden: false })}
                        onMigrateApp={() => dispatcher.dispatch('intializeMigration', { applications: selectedItems })}
                    />

                    <AppsTableWindow data-is-scrollable={!isAccessibleMode}>
                        <ConversationApplicationsTable
                            sortKey={sortKey}
                            items={pageItems}
                            onAppClick={openApp}
                            selection={selection}
                            emptyPlaceholder={getTablePlaceholder()}
                            isLoading={rootStore.luisAppStoreHouse.isLoading}
                            areItemsFiltered={Boolean(query)}
                            overallItemsCount={filteredApps.length}
                            onSortColumn={c => {
                                setSortKey({ key: c.key as keyof LuisAppStore, order: c.isSortedDescending ? 'asc' : 'desc' });
                                selection.setAllSelected(false);
                            }}
                        />
                        <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => handlePageChange(p.selected)} />
                    </AppsTableWindow>
                </AppsTableContainer>

                <ConversationAppCreateModal
                    onSubmit={createApp}
                    disabled={createAppProgressTracker.isRunning}
                    onDismiss={dismissCreateConversationAppDialog}
                    cultures={rootStore.luisCultureStoreHouse.items}
                    hidden={modalsState.isCreateConversationAppModalHidden}
                    areCulturesLoading={rootStore.luisCultureStoreHouse.isLoading}
                />

                <ImportModal
                    fileType={fileTypeToImport}
                    disabled={createAppProgressTracker.isRunning}
                    hidden={modalsState.isImportConversationAppModalHidden}
                    onSubmit={({ file, name }) => importConversationApp(file, name)}
                    dialogContentProps={{ title: localize(luisAppListPageMessages.importApp) }}
                    onDismiss={() => setModalsState({ isImportConversationAppModalHidden: true })}
                />

                <AppLogsImportModal
                    disabled={defaultProgressTracker.isRunning}
                    onSubmit={({ file }) => importConversationAppLogs(file)}
                    hidden={modalsState.isImportConversationLogsModalHidden}
                    onDismiss={() => setModalsState({ isImportConversationLogsModalHidden: true })}
                />

                <DeletePromptModal
                    onDelete={deleteApp}
                    disabled={defaultProgressTracker.isRunning}
                    hidden={modalsState.isDeleteAppModalHidden}
                    onDismiss={() => setModalsState({ isDeleteAppModalHidden: true })}
                    dialogContentProps={{ title: localize(luisAppListPageMessages.deleteAppTitle) }}>
                    <LuisText>{localize(luisAppListPageMessages.deleteAppBody)}</LuisText>
                </DeletePromptModal>

                <RenameModal
                    onSubmit={renameApp}
                    name={selectedItems[0]?.name || ''}
                    disabled={defaultProgressTracker.isRunning}
                    hidden={modalsState.isRenameAppModalHidden}
                    onDismiss={() => setModalsState({ isRenameAppModalHidden: true })}
                    dialogContentProps={{ title: localize(luisAppListPageMessages.renameApp) }}
                />
            </Page>
        </>
    );
});
